import React, { useState } from 'react'
import { Machine } from 'common/types/Machine'
import { Trash, Square, Play, Terminal } from 'feather-icons-react'
import { useVMPanelServerConfig } from '../hooks/VMPanelServerConfig'
import { ForwardInfo } from 'common/types/ForwardInfo'
import ConfirmVMDeletion from './ConfirmVMDeletion'
import { useServerConnection } from '../hooks/ServerConnection'
import { useGitlabAccessToken } from '../hooks/GitlabAccessToken'
import AutoSuspendControls from './AutoSuspendControls'
import { Button, Input, Popup } from 'semantic-ui-react'
import { GITLAB_BASE_URL } from '../config'

const vsCodeInsiders = localStorage.getItem('vsCodeInsiders') === "true"
console.log(`// To use vs code insiders you can set\nlocalStorage.setItem('vsCodeInsiders', 'true')`)

const showTerminalButton = location.protocol === "https:" || location.hostname === "localhost"

const MachineCard = ({
    machine,
    forwardInfo,
}: {
    machine: Machine,
forwardInfo?: ForwardInfo,
}) => {
    const [accessToken] = useGitlabAccessToken()
    const serverConnection = useServerConnection()
    const serverConfig = useVMPanelServerConfig()

    const { config } = machine
    let { http, ssh } = config

    if(!http) {
        http = {}
    }
    if(!ssh) {
        ssh = {}
    }

    const [confirmDelete, setConfirmDelete] = useState(false);
    const originalName = machine.name2 ? machine.name2 : `${machine.namespace} / ${machine.project}`
    const [machineName, setMachineName] = useState(originalName)
    const [editMachineName, setEditMachineName] = useState(false)

    return (
        <div
            className='rounded-xl bg-base-100 border-2 border-base-300 p-2 shadow-xl'
            style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
            }}
        >
            <div style={{
                display: "flex",
                flexDirection: "column",
                overflow: "auto"
            }}>
                <div className='flex flex-col gap-2'>
                    <div style={{
                        display: "flex",
                        justifyContent: "space-between",
                    }}>
                        {
                            editMachineName ? (
                                <div style={{
                                    flex: 1,
                                    display: "flex",
                                }}>
                                    <Input
                                        style={{
                                            flex: 1,
                                        }}
                                        value={machineName}
                                        onChange={(e) => setMachineName(e.target.value)}
                                    />
                                    <Button
                                        icon="check"
                                        onClick={async () => {
                                            if(machineName.length > 0) {
                                                if(machineName !== originalName) {
                                                    await serverConnection.renameVM(accessToken, machine._id, machineName)
                                                }
                                                setEditMachineName(false)
                                            }
                                        }}
                                    />
                                    <Button
                                        icon="close"
                                        onClick={() => {
                                            setMachineName(originalName)
                                            setEditMachineName(false)
                                        }}
                                    />
                                </div>
                            ) : (
                                <span onDoubleClick={() => setEditMachineName(true)} className='font-bold'>{machineName}</span>
                            )
                        }
                        <a
                            style={{
                                marginLeft: 5,
                                display: "block",
                                backgroundImage: "url('/public/gitlab.svg')",
                                backgroundSize: "contain",
                                backgroundRepeat: "no-repeat",
                                backgroundPosition: "center",
                                width: 20,
                                height: 20,
                            }}
                            href={machine.ref
                                  ? `${GITLAB_BASE_URL}/${machine.namespace}/${machine.project}/-/tree/${machine.ref}`
                                  : `${GITLAB_BASE_URL}/${machine.namespace}/${machine.project}`}
                            target="_blank"
                        >
                        </a>
                    </div>
                    <div>
                        <div style={{
                            display: "flex",
                            alignItems: "baseline",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                            gap: "5px",
                        }}>
                            {
                                machine.alternativeConfigName && (
                                    <div>
                                        {machine.alternativeConfigName}
                                    </div>
                                )
                            }
                            {
                                machine.ref && (
                                    <div style={{
                                        fontSize: "13px"
                                    }}>
                                        {machine.ref}
                                    </div>
                                )
                            }
                        </div>
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}>
                            <span className='text-sm'>{machine._id}</span>
                            <div style={{
                                marginLeft: 10,
                                padding: 5,
                                whiteSpace: "nowrap",
                                background: "#e3e3e3",
                                borderRadius: 4,
                            }}>
                                {machine.state.type}
                            </div>
                        </div>
                    </div>
                </div>
                <AutoSuspendControls machine={machine} />
                <div style={{
                    overflow: "auto"
                }}>
                    {
                        forwardInfo && (
                            <>
                                {
                                    Object.keys(http).length > 0 && (
                                        <div className='flex gap-2'>
                                            <span className='font-semibold '>
                                                http:
                                            </span>
                                            <div style={{
                                                display: "flex",
                                                flexWrap: "wrap",
                                                gap: "0 5px",
                                            }}>
                                                {
                                                    Object.keys(http).map((name) => {
                                                        const { path } = http[name]
                                                        return (
                                                            <div key={name} style={{
                                                                textDecoration: "underline"
                                                            }}>
                                                                <a href={`https://vm${machine._id}-${name}${serverConfig.domainSuffix}${path || "/"}`} target='_blank'>{name}</a>
                                                            </div>
                                                        )
                                                    })
                                                }
                                            </div>
                                        </div>
                                    )
                                }
                                {
                                    forwardInfo && Object.keys(forwardInfo.ssh).length > 0 && (
                                        <div className='flex gap-2'>
                                            <span className=' font-semibold'>
                                                ssh:
                                            </span>
                                            <div className=''>
                                                {
                                                    Object.keys(ssh)
                                                        .filter((name) => {
                                                            return Boolean(forwardInfo.ssh[name])
                                                        })
                                                        .map((name) => {
                                                            const port = forwardInfo.ssh[name]
                                                            const info = ssh[name]
                                                            const vscodeLink = `${vsCodeInsiders ? "vscode-insiders" : "vscode"}://vscode-remote/ssh-remote+${info.user || "root"}@vm${machine._id}-${name}${serverConfig.domainSuffix}:${port}${info.vscodePath}?windowId=_blank#name=${encodeURIComponent(machineName)}`

                                                            return (
                                                                <div
                                                                    key={name}
                                                                    style={{
                                                                        display: "flex",
                                                                        alignItems: "center"
                                                                    }}
                                                                >
                                                                    <div>
                                                                        {name}
                                                                    </div>
                                                                    <div style={{
                                                                        marginLeft: 5,
                                                                    }}>
                                                                        {port}
                                                                    </div>
                                                                    <a
                                                                        style={{
                                                                            marginLeft: 5,
                                                                            display: "block",
                                                                            backgroundImage: "url('/public/code.svg')",
                                                                            backgroundSize: "contain",
                                                                            backgroundRepeat: "no-repeat",
                                                                            backgroundPosition: "center",
                                                                            width: 20,
                                                                            height: 20,
                                                                        }}
                                                                        href={vscodeLink}
                                                                    ></a>
                                                                    {
                                                                        showTerminalButton && (
                                                                            <div
                                                                                style={{
                                                                                    cursor: "pointer"
                                                                                }}
                                                                                onClick={() => {
                                                                                    const sshCommand = `ssh ${info.user || "root"}@vm${machine._id}-${name}${serverConfig.domainSuffix} -p ${port}`
                                                                                    navigator.clipboard.writeText(sshCommand)
                                                                                }}
                                                                            >
                                                                                <Popup
                                                                                    inverted
                                                                                    hideOnScroll
                                                                                    position="top center"
                                                                                    content="Сopied"
                                                                                    on="click"
                                                                                    trigger={<Terminal width={20} height={20}/>}
                                                                                />
                                                                            </div>
                                                                        )
                                                                    }
                                                                </div>
                                                            )
                                                        })
                                                }
                                            </div>
                                        </div>
                                    )
                                }
                            </>
                        )
                    }
                </div>
            </div>
            {/* <div className='flex gap-3'>
                <button className='btn btn-success'>V</button>
                <button className='btn btn-error'>X</button>
            </div> */}
            <div className='flex gap-2 text-white '>
                {machine.state.type !== "removing" && (
                    <button
                        className='bg-error p-1 w-fit rounded-md'
                        onClick={() => setConfirmDelete(true)}
                    >
                        <Trash />
                    </button>
                )}
                {
                    machine.state.type === "running" && (
                        <button
                            className='bg-primary text-white p-1 w-fit rounded-md'
                            onClick={() => serverConnection.stopVM(accessToken, machine._id)}
                        >
                            <Square />
                        </button>
                    )
                }
                {
                    machine.state.type === "stopped" && (
                        <button
                            className='bg-secondary-content p-1 w-fit rounded-md'
                            onClick={() => serverConnection.startUserVM(accessToken, machine._id)}
                        >
                            <Play />
                        </button>
                    )
                }
            </div>
            {confirmDelete && <ConfirmVMDeletion machineId={machine._id} onClose={() => setConfirmDelete(false)} />}
        </div>
    )
}

export default MachineCard
